import { useId, useRef, useState } from 'react';
import {
  useFloating,
  useListNavigation,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingList,
  flip,
  offset,
  useTransitionStyles,
} from '@floating-ui/react';
import clsx from 'clsx';

import { AngleDownIcon } from '@components/icons/AngleDownIcon';

import { SelectOption } from './SelectOption';

/**
 * Re-usable Select component
 *
 * @param {{
 *   id?: string;
 *   label: string;
 *   placeholder: string;
 *   options: { value: string | null, label: string }[];
 *   value: string | null;
 *   onChange: (value: string) => void;
 *   className?: string;
 * }} props
 *
 * @return {JSX.Element}
 */
export function Select({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  className,
}) {
  const triggerId = useId();
  const listRef = useRef([]);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const { context, refs, floatingStyles } = useFloating({
    open,
    placement: 'bottom-start',
    onOpenChange: setOpen,
    middleware: [offset({ mainAxis: 8 }), flip()],
  });

  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });

  const role = useRole(context, { role: 'listbox' });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNav, role, click, dismiss],
  );

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 300,
  });

  const handleSelect = (value) => {
    onChange(value);
    setOpen(false);
  };

  const selectedOption = options.find((item) => item.value === value);
  const showPlaceholder = !selectedOption?.value;

  return (
    <div className={clsx('relative w-fit', className)}>
      <label htmlFor={id ?? triggerId} className="sr-only">
        {label}
      </label>

      <button
        ref={refs.setReference}
        type="button"
        id={id ?? triggerId}
        className={clsx(
          'w-full',
          'py-[4px] px-[16px]',
          'flex flex-row justify-between items-center gap-[8px]',
          'text-start text-[16px] leading-normal',
          showPlaceholder ? 'text-white/80' : 'text-white',
          'placeholder:text-white/40',
          'rounded-[10px]',
        )}
        style={{ border: '1px solid rgba(255, 255, 255, 0.2)' }}
        {...getReferenceProps()}
      >
        <span>{showPlaceholder ? placeholder : selectedOption.label}</span>

        <span
          className={clsx(
            'relative top-[1px]',
            'transform transition duration-300',
            open && '-rotate-180 top-0',
          )}
        >
          <AngleDownIcon color="#ffffff" size={16} />
        </span>
      </button>

      {isMounted && (
        <FloatingList elementsRef={listRef}>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, minWidth: '100%', zIndex: 10 }}
            {...getFloatingProps()}
          >
            <div
              className={clsx(
                'flex flex-col gap-[6px]',
                'py-[8px] px-[4px]',
                'bg-neutral-900',
                'rounded-[10px]',
                'shadow-md shadow-white/20',
              )}
              style={transitionStyles}
            >
              <span className="inline-block px-[16px] text-[12px] leading-normal text-white/60">
                {label}
              </span>
              {options.map((option) => (
                <SelectOption
                  key={option.value}
                  activeIndex={activeIndex}
                  isSelected={selectedOption?.value === option.value}
                  option={option}
                  onSelect={handleSelect}
                  getItemProps={getItemProps}
                />
              ))}
            </div>
          </div>
        </FloatingList>
      )}
    </div>
  );
}
